import { UUID } from 'crypto'
import {
  Application,
  ApplicationTrackingData,
  SaveAnswerFn,
  ApplicationTrackingUpdateData,
  ApplicationSummary,
  ApplicationQuestionsUpdateData,
  AddApplicationForTracking,
} from '../models/Applications'

import { RegeneratedAnswer, RegenerateAnswerFn } from '../models/Applications'
import { throwIfProfileRegenerating } from './profileRegenError'
import { AnswerValidationError } from './answerValidationError'

// The validation detail lists every option a question offers, which runs long
// on a question with many; enough to name the problem is enough to show.
const MAX_ERROR_DETAIL_CHARS = 300

const serverURL = import.meta.env.VITE_API_URL

/**
 * Build a client API URL consistently
 * Handles both base URLs with and without /v1/client prefix
 */
function buildClientApiUrl(endpoint: string): string {
  const baseUrl = serverURL.endsWith('/') ? serverURL.slice(0, -1) : serverURL
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint

  if (baseUrl.includes('/v1/client')) {
    return `${baseUrl}/${cleanEndpoint}`
  }
  return `${baseUrl}/v1/client/${cleanEndpoint}`
}

export async function getApplicationsByOrg(
  token: string,
  orgUuid: UUID,
): Promise<Application[]> {
  const response = await fetch(`${serverURL}/applications/${orgUuid}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data
}

export async function getApplicationsByNeed(
  token: string,
  needId: number,
  orgUuid: UUID,
): Promise<ApplicationTrackingData[]> {
  const response = await fetch(
    `${serverURL}/applications/${orgUuid}/${needId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data
}

export async function fetchLatestApplicationByNeedOpp(
  token: string,
  fundingNeedId: number,
  oppUuid: UUID,
) {
  const response = await fetch(
    `${serverURL}/applications/latest/${fundingNeedId}/${oppUuid}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data
}

interface CreateApplicationFn {
  token: string
  scanId: number
}

export async function createApplication({
  token,
  scanId,
}: CreateApplicationFn): Promise<{ job_id: string; status: string }> {
  const response = await fetch(`${serverURL}/applications/${scanId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export async function regenerateAnswer({
  token,
  applicationId,
  answerIndex,
  answerFeedback,
  regeneratedAnswer,
}: RegenerateAnswerFn): Promise<RegeneratedAnswer> {
  const response = await fetch(
    `${serverURL}/applications/regenerate/${applicationId}/${answerIndex}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answer_feedback: answerFeedback,
        regenerated_answer: regeneratedAnswer || null,
      }),
    },
  )

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const newAnswer = await response.json()

  return {
    newAnswer: newAnswer.answer.answer,
    newReasoning: newAnswer.answer.reasoning,
    newFollowUp: newAnswer.answer.follow_up_comments,
  }
}

export async function saveAnswer({
  token,
  applicationId,
  answerIndex,
  newAnswer,
}: SaveAnswerFn): Promise<void> {
  const response = await fetch(
    `${serverURL}/applications/regenerate/${applicationId}/${answerIndex}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_answer: newAnswer.newAnswer,
        new_reasoning: newAnswer.newReasoning,
        new_follow_up: newAnswer.newFollowUp,
        regeneration_feedback: newAnswer.regenerationFeedback || null,
      }),
    },
  )

  if (!response.ok) {
    // Surface the detail for a 400 only. That is the answer-validation message,
    // which names the question's own options and the selections the client just
    // sent, so it tells the user what to change without revealing anything they
    // did not already have. Any other status can carry server-side information
    // that should not reach the UI, and a 422 returns detail as an array rather
    // than a string, so those stay generic.
    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}))
      const detail = errorData?.detail
      if (typeof detail === 'string' && detail.trim()) {
        throw new AnswerValidationError(detail.slice(0, MAX_ERROR_DETAIL_CHARS))
      }
    }
    throw new Error('Failed to save answer')
  }
}

export async function emailApplication(token: string, applicationId: number) {
  const response = await fetch(
    `${serverURL}/applications/${applicationId}/email`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    return { success: false, error: 'Failed to email application' }
  }

  return { success: true, error: null }
}

// Get single application with tracking data
export async function getApplication(
  token: string,
  applicationId: number,
  orgUuid: UUID,
): Promise<Application> {
  const response = await fetch(
    `${serverURL}/applications/${orgUuid}/application/${applicationId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

// Get summary data for org applications
export async function getApplicationsSummary(
  token: string,
  orgUuid: UUID,
): Promise<ApplicationSummary[]> {
  const response = await fetch(`${serverURL}/applications/${orgUuid}/summary`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export async function updateApplicationTrackingData({
  token,
  orgUuid,
  applicationId,
  data,
}: ApplicationTrackingUpdateData) {
  const response = await fetch(
    `${serverURL}/applications/${orgUuid}/${applicationId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
}

export async function updateApplicationQuestions({
  token,
  orgUuid,
  applicationId,
}: ApplicationQuestionsUpdateData) {
  if (!applicationId || !orgUuid) {
    throw new Error('Missing applicationId or orgUuid')
  }

  const response = await fetch(
    `${serverURL}/applications/questions/${orgUuid}/${applicationId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
}

export async function addApplicationForTracking({
  token,
  orgUuid,
  needId,
  application,
}: AddApplicationForTracking) {
  const response = await fetch(
    `${serverURL}/applications/add/${orgUuid}/${needId}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(application),
    },
  )

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error('Application already exists')
    }
    throw new Error(`HTTP error! status: ${response.status}`)
  }
}

/**
 * Get scan_result_id for an application by matching org, need, and opportunity
 * Returns null if no matching scan result is found
 */
export async function getScanResultId(
  token: string,
  orgUuid: string,
  needId: number,
  opportunityUuid: string,
): Promise<number | null> {
  try {
    const url = buildClientApiUrl(
      `scans/scan-result/${orgUuid}/${needId}/${opportunityUuid}`,
    )

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      if (response.status !== 404) {
        console.error(
          `Failed to fetch scan result ID with status: ${response.status}`,
        )
      }
      // If not found (404) or any other error, return null
      return null
    }

    const data = await response.json()
    return data.id || null
  } catch (error) {
    // If any error occurs, return null so feedback can still be submitted
    console.error('Error fetching scan result ID')
    return null
  }
}
